import React, { useCallback, useEffect, useRef, useState } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";

const TabsBar = styled.nav`
    ${tw`w-full`}
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 13px;
    padding: 0px 0px 7px;
    font-family: "Roboto Condensed", sans-serif;
    background-color: #fffbdc;

    @media (min-width: 768px) {
        padding: 0px 40px 16px;
        gap: 28px;
    }
`;

const TabBtn = styled.button`
    ${tw`relative font-semibold`}
    background: transparent;
    border: 0;
    cursor: pointer;
    outline: none;
    padding: 5px 2px 12px;
    font-size: clamp(1.34rem, 1.26rem + 0.19vw, 1.6rem);
    line-height: 1;
    color: ${(p) => (p.$active ? "#14133B" : "#9CA3AF")};
    transition: color 0.12s ease;

    & > span {
        font-weight: 400;
        display: inline-block;
        transform-origin: center;
    }

    &::after {
        content: "";
        position: absolute;
        left: 50%;
        bottom: 2px;
        width: ${(p) => p.$dotSize ?? 8}px;
        height: ${(p) => p.$dotSize ?? 8}px;
        background: #14133b;
        border-radius: 50%;
        transform: translateX(-50%) scale(${(p) => (p.$active ? 1 : 0)});
        opacity: ${(p) => (p.$active ? 1 : 0)};
        transition: ${(p) =>
            p.$active ? "none" : "transform .18s ease, opacity .18s ease"};
    }

    &:hover::after {
        transform: translateX(-50%) scale(1);
        opacity: ${(p) => (p.$active ? 1 : 0.5)};
    }

    @media (min-width: 768px) {
        padding: 10px 6px 18px;
        font-size: clamp(1.98rem, 1.92rem + 0.2vw, 2.12rem);
        &::after {
            bottom: 4px;
        }
    }
`;

const PageWrap = tw.div`w-full bg-white text-dark-500`;
const SectionSpacer = tw.div`mt-8`;

const SectionContainer = styled.section(({ $bgColor }) => [
    tw`relative -mx-8 -mt-8 font-work_sans`,
    css`
        background-color: ${$bgColor};
    `,
]);

const SectionInner = styled.div(({ $reverse }) => [
    tw`flex flex-col-reverse lg:flex-row text-gray-900 min-h-screen`,
    $reverse &&
        css`
            @media (min-width: 1024px) {
                flex-direction: row-reverse;
            }
        `,
]);

const LeftColumn = styled.div`
    ${tw`px-4 lg:px-6 xl:px-8 lg:w-1/2 flex flex-col justify-center lg:min-h-0`}
    padding-top: 2.1rem;
    padding-bottom: 2.1rem;

    @media (min-width: 768px) {
        padding-top: 2rem;
        padding-bottom: 3rem;
    }
`;

const Content = styled.div`
    ${tw`flex flex-col items-start text-left`}

    @media (min-width: 1024px) {
        margin-top: 1rem;
        margin-bottom: 1.5rem;
    }
`;

const Heading = styled.h2`
    ${tw`tracking-tight`}
    font-size: 3rem;
    line-height: 0.8;
    padding-bottom: 14px;
    @media (min-width: 768px) {
        font-size: clamp(3rem, 2.8rem + 1.76vw, 6.6rem);
        line-height: 0.98;
    }
`;

const Paragraph = styled.p`
    margin-top: 1.05rem;
    margin-bottom: 1.05rem;
    max-width: 42rem;
    font-family: "Roboto Condensed", sans-serif;
    font-weight: 200;
    font-size: 3rem;
    line-height: 1;

    @media (min-width: 1024px) {
        margin-top: 2rem;
        font-size: clamp(4rem, 4rem + 1.814vw, 6rem);
        margin-bottom: 2rem;
    }
`;

const MediaColumn = styled.div`
    ${tw`w-full`}
    position: relative;
    flex: 1 1 auto;
    width: 100%;
    min-height: 50vh;
    @media (min-width: 1024px) {
        flex: none;
        width: 50%;
        min-height: 60vh;
    }
    background: #0b1f3a;
`;

const MediaImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
`;

const InfoBar = styled.div`
    ${tw`w-full`}
    background-color: #fffbdc;
`;

const InfoBarInner = styled.div`
    ${tw`max-w-5xl mx-auto`}
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    color: #14133b;
    padding-top: calc(clamp(1.925rem, 1.61rem + 0.98vw, 2.625rem) * 2);
    padding-bottom: calc(clamp(1.925rem, 1.61rem + 0.98vw, 2.625rem) * 1.5);
    padding-left: clamp(1.75rem, 1.4rem + 1vw, 3rem);
    padding-right: clamp(1.75rem, 1.4rem + 1vw, 3rem);
`;

const InfoTitle = styled.h3`
    ${tw`text-2xl sm:text-3xl`}
    
    line-height: 1.1;
    font-family: "Roboto Condensed", sans-serif;
    font-weight: 400;
    margin: 0;
`;

const InfoDescription = styled.p`
    ${tw`text-base sm:text-lg leading-relaxed`}
    font-family: "Work Sans", sans-serif;
    margin: 0;
    padding-bottom: clamp(1.925rem, 1.61rem + 0.98vw, 2.625rem);
    padding-left: 15%;
`;

const SECTION_ITEMS = [
    {
        id: "our-story",
        title: "Our Story",
        description:
            "Grain Mediterranean was born from a quest to redefine fast food in the UK.",
        background: "#fdd96e",
        imagePlacement: "right",
        image:
            "https://uk-grain.oss-eu-west-1.aliyuncs.com/foundation/9081762980371_.pic_hd.jpg",
        infoTitle:
            "At Grain, every meal is a step towards better health and a more connected world.",
        infoDescription:
            "Inspired by the longevity and vitality of Mediterranean Blue Zone communities, we set out to create meals that are as nourishing as they are delicious. Our menu centres on hearty wholegrains, fresh produce, and clean proteins prepared without seed oils to support wholebody health. But it’s more than just food; it’s about fostering a community that values honesty, wholesomeness, and heart.",
    },
    {
        id: "sustainability",
        title: "Sustainability",
        description: "At Grain, caring for the planet starts in our own kitchens.",
        background: "#d9e1a2",
        imagePlacement: "left",
        image:
            "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/1MB%E9%A6%96%E9%A1%B5%E8%BD%AE%E6%92%AD%E5%9B%BE/5721761168782_.pic.jpg",
        infoTitle:
            "We focus on reducing waste, using energy efficiently, and building a work culture that values balance and wellbeing.",
        infoDescription:
            "Sustainability is reflected in the way we work and in the care we show to the people behind every bowl.",
    },
    {
        id: "community",
        title: "Community",
        description: "At Grain, giving back is part of who we are.",
        background: "#b2cac7",
        imagePlacement: "right",
        image:
            "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/story/2271759976955_.pic.jpg",
        infoTitle:
            "Through the Grain Foundation, our store aims to help improve nutrition education in UK schools and supports global water and health initiatives.",
        infoDescription:
            "Because being Healthy, Hearty, Honest isn’t only about what we serve; it’s also about how we care for the people around us.",
    },
];

const OurStoryComponent = () => {
    const [activeTab, setActiveTab] = useState(SECTION_ITEMS[0].id);
    const sectionRefs = useRef({});

    const scrollToSection = useCallback((id, behavior = "smooth") => {
        if (!id) return;
        setActiveTab(id);
        const target = sectionRefs.current[id];
        if (target) {
            target.scrollIntoView({ behavior, block: "start" });
        }
    }, []);

    const handleTabClick = useCallback(
        (id) => {
            scrollToSection(id, "smooth");
        },
        [scrollToSection]
    );

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const intersecting = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort(
                        (a, b) =>
                            a.target.getBoundingClientRect().top -
                            b.target.getBoundingClientRect().top
                    );

                const nextEntry = intersecting[0];
                if (nextEntry?.target?.dataset?.sectionId) {
                    setActiveTab((prev) =>
                        prev === nextEntry.target.dataset.sectionId
                            ? prev
                            : nextEntry.target.dataset.sectionId
                    );
                }
            },
            {
                threshold: [0, 0.25, 0.5, 0.75],
                rootMargin: "-10% 0px -45% 0px",
            }
        );

        SECTION_ITEMS.forEach(({ id }) => {
            const node = sectionRefs.current[id];
            if (node) {
                observer.observe(node);
            }
        });

        const handleScrollTop = () => {
            if (window.scrollY <= 5) {
                setActiveTab((prev) =>
                    prev === SECTION_ITEMS[0].id ? prev : SECTION_ITEMS[0].id
                );
            }
        };

        window.addEventListener("scroll", handleScrollTop, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener("scroll", handleScrollTop);
        };
    }, []);

    useEffect(() => {
        const handleHashNavigation = () => {
            const hash = window.location.hash.replace("#", "");
            if (hash && sectionRefs.current[hash]) {
                scrollToSection(hash, "smooth");
            }
        };

        handleHashNavigation();
        window.addEventListener("hashchange", handleHashNavigation);

        return () => {
            window.removeEventListener("hashchange", handleHashNavigation);
        };
    }, [scrollToSection]);
    const tabsDotSize = 8;

    return (
        <PageWrap>
            <TabsBar>
                {SECTION_ITEMS.map(({ id, title }) => (
                    <TabBtn
                        key={id}
                        type="button"
                        onClick={() => handleTabClick(id)}
                        $active={activeTab === id}
                        $dotSize={tabsDotSize}
                    >
                        <span>{title}</span>
                    </TabBtn>
                ))}
            </TabsBar>

            <SectionSpacer />

            {SECTION_ITEMS.map(
                ({
                     id,
                     title,
                     description,
                     background,
                     imagePlacement,
                     image,
                     infoTitle,
                     infoDescription,
                 }) => (
                    <SectionContainer
                        key={id}
                        id={id}
                        data-section-id={id}
                        ref={(node) => {
                            if (node) {
                                sectionRefs.current[id] = node;
                            }
                        }}
                        $bgColor={background}
                    >
                        <SectionInner $reverse={imagePlacement === "left"}>
                            <LeftColumn>
                                <Content>
                                    <Heading>{title}</Heading>
                                    <Paragraph>{description}</Paragraph>
                                </Content>
                            </LeftColumn>

                            <MediaColumn aria-hidden="true">
                                <MediaImage src={image} alt="" loading="lazy" />
                            </MediaColumn>
                        </SectionInner>

                        <InfoBar>
                            <InfoBarInner>
                                <InfoTitle>{infoTitle}</InfoTitle>
                                <InfoDescription>{infoDescription}</InfoDescription>
                            </InfoBarInner>
                        </InfoBar>
                    </SectionContainer>
                )
            )}
        </PageWrap>
    );
};

export default OurStoryComponent;
